import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '@/utils/apiClient';


/**
 * SIMPLIFIED PROTECTED ROUTE - ONLY checks authentication
 * 
 * ALL ROUTING LOGIC is now centralized in DashboardRedirect (App.js)
 * This component ONLY:
 * 1. Checks if user has valid token
 * 2. Validates token with backend
 * 3. Blocks non-admin users from admin routes
 * 
 * NO PROFILE/PHONE/IDENTITY REDIRECTS - That's DashboardRedirect's job
 */
function ProtectedRoute({ children }) {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    loading: true,
    isAuthenticated: false,
    user: null,
    redirectTo: null
  });

  // ADMIN-ONLY ROUTES - Block non-admin users completely
  const ADMIN_ONLY_PATHS = ['/admin', '/admin/super'];

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // No token? Go to login — pero si el enlace trae ?wa=<phone> (viene desde
        // WhatsApp del bot con auto-login), saltamos directo a "iniciar sesión con
        // tu número" prellenado, sin pasar por /login.
        if (!token) {
          const params = new URLSearchParams(location.search);
          const wa = (params.get('wa') || '').replace(/\D/g, '');
          if (wa) {
            const next = `${location.pathname}${location.search}`;
            setAuthState({
              loading: false,
              isAuthenticated: false,
              user: null,
              redirectTo: `/phone-register?prefill=${wa}&next=${encodeURIComponent(next)}`
            });
            return;
          }
          setAuthState({
            loading: false,
            isAuthenticated: false,
            user: null,
            redirectTo: '/login'
          });
          return;
        }
        
        // Validate token with backend
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const user = response.data;
        localStorage.setItem('user', JSON.stringify(user));
        
        // ADMIN ROUTE PROTECTION
        const requestedPath = location.pathname;
        const isAdminRoute = ADMIN_ONLY_PATHS.some(p => requestedPath === p || requestedPath.startsWith(p + '/'));
        
        if (isAdminRoute && user.role !== 'admin' && user.role !== 'super_admin') {
          console.warn('Unauthorized access attempt to admin route:', requestedPath);
          setAuthState({
            loading: false,
            isAuthenticated: true,
            user,
            redirectTo: '/'
          });
          return;
        }
        
        // TOKEN VALID - Allow access
        // All routing logic (profile setup, driver registration, etc.) 
        // is handled by DashboardRedirect in App.js
        setAuthState({
          loading: false,
          isAuthenticated: true,
          user,
          redirectTo: null
        });
        
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Mismo trato: si el enlace trae ?wa=<phone>, auto-login con número.
        const params = new URLSearchParams(location.search);
        const wa = (params.get('wa') || '').replace(/\D/g, '');
        if (wa) {
          const next = `${location.pathname}${location.search}`;
          setAuthState({
            loading: false,
            isAuthenticated: false,
            user: null,
            redirectTo: `/phone-register?prefill=${wa}&next=${encodeURIComponent(next)}`
          });
          return;
        }
        setAuthState({
          loading: false,
          isAuthenticated: false,
          user: null,
          redirectTo: '/login'
        });
      }
    };
    
    checkAuth();
  }, [location.pathname]);

  // Loading state
  // Loading state
  if (authState.loading) {
    return (
      <div className="vd-loader">
        <div className="vd-loader-mark" aria-hidden="true">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.6-1.6-1.9l-3.4-.8c-.5-.1-1-.5-1.2-1L14.4 7c-.2-.4-.6-.7-1.1-.7H11c-.5 0-.9.3-1.1.7l-1.4 2.3c-.2.5-.7.9-1.2 1l-3.4.8c-.9.3-1.6 1-1.6 1.9v3c0 .6.4 1 1 1h2" />
            <circle cx="7" cy="17" r="2" />
            <circle cx="17" cy="17" r="2" />
          </svg>
        </div>
        <div className="vd-loader-logo">
          Vía<span>Directa</span>
        </div>
        <div className="vd-loader-bar">
          <div className="vd-loader-bar-inner" />
        </div>
        <p className="vd-loader-text">Verificando sesión...</p>
      </div>
    );
  }

  // Redirect if needed
  if (authState.redirectTo) {
    return <Navigate to={authState.redirectTo} replace />;
  }

  // All checks passed - render protected content
  return children;
}

export default ProtectedRoute;
